import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Quote, Star, MessageSquareQuote } from 'lucide-react';

const TESTIMONIALS = [
  {
    id: 1,
    name: 'Prospective Scholar',
    role: 'Science Student',
    avatar: '👨‍🎓',
    rating: 5,
    content: "The trust's scholarship initiative represents a beacon of hope for students like me who dream of pursuing advanced studies. Their commitment to academic excellence inspires us to work harder.",
    accentColor: 'border-brand-indigo/20',
    quoteColor: 'text-brand-indigo/40',
  },
  {
    id: 2,
    name: 'Education Coordinator',
    role: 'Community Volunteer',
    avatar: '👩‍🏫',
    rating: 5,
    content: "Meenamma Educational Trust's dedication to fostering educational opportunities in our region is remarkable. Their systematic approach to student support will create lasting positive change.",
    accentColor: 'border-brand-emerald/20',
    quoteColor: 'text-brand-emerald/40',
  },
  {
    id: 3,
    name: 'Supporter',
    role: 'Social Contributor',
    avatar: '🤝',
    rating: 5,
    content: "I admire the trust's methodology in identifying deserving candidates and providing meaningful assistance. Their focus on merit and need-based support reflects genuine commitment to education.",
    accentColor: 'border-brand-amber/20',
    quoteColor: 'text-brand-amber/40',
  },
  {
    id: 4,
    name: 'Guardian',
    role: 'Local Resident',
    avatar: '👨‍👩‍👧‍👦',
    rating: 5,
    content: "Having an organisation like Meenamma Educational Trust in our community fills me with optimism about my children's future. Their work opens doors that seemed impossible before.",
    accentColor: 'border-brand-rose/20',
    quoteColor: 'text-brand-rose/40',
  },
];

const cardVar = {
  hidden:  { opacity: 0, y: 30, scale: 0.97 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.13, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};
const fadeUp = { hidden: { opacity: 0, y: -18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55 } } };

const StarRating = ({ count }) => (
  <div className="flex gap-0.5" aria-label={`${count} out of 5 stars`}>
    {Array.from({ length: count }).map((_, j) => (
      <Star key={j} className="h-3.5 w-3.5 text-brand-amber fill-current" />
    ))}
  </div>
);

const TestimonialsSection = () => (
  <section className="py-20 md:py-28 bg-gradient-to-b from-brand-emerald/5 to-background">
    <div className="container mx-auto px-4 sm:px-6">

      {/* Badge + Header */}
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="flex justify-center mb-4">
          <span className="section-badge-emerald">
            <MessageSquareQuote className="h-3 w-3" />
            Community Voices
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-5">
          What People <span className="gradient-text">Say About Us</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Voices from our community — students, families, and advocates who share our
          vision of educational empowerment.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.id}
            custom={i}
            variants={cardVar}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            whileHover={{ y: -5 }}
            transition={{ type: 'spring', stiffness: 280, damping: 22 }}
          >
            <Card className={`h-full glassmorphism-card border ${t.accentColor} shadow-sm
                             hover:shadow-lg transition-all duration-300`}>
              <CardContent className="p-7 flex flex-col h-full">

                {/* Top row: quote icon + stars */}
                <div className="flex items-center justify-between mb-5">
                  <div className={`p-2.5 rounded-xl bg-current/8 ${t.quoteColor} bg-opacity-10`}
                       style={{ background: 'hsl(var(--muted))' }}>
                    <Quote className={`h-5 w-5 ${t.quoteColor}`} />
                  </div>
                  <StarRating count={t.rating} />
                </div>

                {/* Quote text */}
                <blockquote className="text-[14px] text-muted-foreground leading-relaxed
                                       italic flex-grow mb-6 relative">
                  <span className="text-foreground/12 text-6xl font-serif leading-none absolute -top-2 -left-1 select-none">
                    "
                  </span>
                  <span className="relative">{t.content}</span>
                </blockquote>

                {/* Divider */}
                <div className="section-divider mb-5" />

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center
                                  text-2xl flex-shrink-0 shadow-inner">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-bold text-foreground text-sm leading-tight">{t.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.p
        className="text-center mt-10 text-xs text-muted-foreground/60 italic"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7 }}
      >
        * Testimonials reflect community enthusiasm for our educational initiatives.
      </motion.p>
    </div>
  </section>
);

export default TestimonialsSection;
